import { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  COUNTRIES, DEFAULT_COUNTRY, type Country, applyBrMask, onlyDigits, detectCountry, extractLocalDigits,
} from '@/lib/phone';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

interface Props {
  value?: string;
  onChange: (phone: string) => void;
  onLocale?: (locale: string) => void;
  placeholder?: string;
}

/**
 * Telefone com seletor de DDI (bandeira) e máscara BR. O país também define o
 * locale do sistema (onLocale). Emite no formato armazenado: "+DDIdígitos".
 * Aceita `value` inicial ("+DDIdigits") para preencher (ex.: edição de perfil).
 */
export function PhoneInput({ value, onChange, onLocale, placeholder }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const initCountry = value ? detectCountry(value) : DEFAULT_COUNTRY;
  const initDigits = value ? extractLocalDigits(value, initCountry) : '';
  const [country, setCountry] = useState<Country>(initCountry);
  const [digits, setDigits] = useState(initDigits);
  const [display, setDisplay] = useState(initDigits ? (initCountry.code === 'BR' ? applyBrMask(initDigits) : initDigits) : '');

  // Sincroniza quando o `value` externo chega/muda (ex.: perfil carregou).
  useEffect(() => {
    if (value === undefined) return;
    const c = value ? detectCountry(value) : DEFAULT_COUNTRY;
    const d = value ? extractLocalDigits(value, c) : '';
    setCountry(c);
    setDigits(d);
    setDisplay(d ? (c.code === 'BR' ? applyBrMask(d) : d) : '');
  }, [value]);

  const pickCountry = (c: Country) => {
    setCountry(c);
    setDisplay(c.code === 'BR' ? applyBrMask(digits) : digits);
    onChange(digits ? `${c.ddi}${digits}` : '');
    onLocale?.(c.locale);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleInput = (raw: string) => {
    const d = country.code === 'BR' ? onlyDigits(raw).slice(0, 11) : onlyDigits(raw);
    setDigits(d);
    setDisplay(country.code === 'BR' ? applyBrMask(d) : d);
    onChange(d ? `${country.ddi}${d}` : '');
  };

  const ph = placeholder ?? (country.code === 'BR' ? '(11) 99999-9999' : 'somente números');

  return (
    <div className="flex h-9 overflow-hidden rounded-md border border-input bg-transparent shadow-sm focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/40">
      <DropdownMenu>
        <DropdownMenuTrigger className="flex shrink-0 items-center gap-1 border-r border-input bg-muted px-3 outline-none transition-colors hover:bg-muted/70">
          <span className="text-lg leading-none">{country.flag}</span>
          <ChevronDown className="size-3 text-muted-foreground" />
        </DropdownMenuTrigger>
        <DropdownMenuContent className="max-h-72 min-w-[16rem] overflow-y-auto">
          {COUNTRIES.map((c) => (
            <DropdownMenuItem
              key={c.code}
              onClick={() => pickCountry(c)}
              className={cn('gap-3', country.code === c.code && 'bg-primary/10 font-medium text-primary')}
            >
              <span className="text-lg">{c.flag}</span>
              <span className="w-10 shrink-0 font-mono text-xs text-muted-foreground">{c.ddi}</span>
              <span>{c.name}</span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <input
        ref={inputRef}
        type="tel"
        inputMode="numeric"
        placeholder={ph}
        value={display}
        onChange={(e) => handleInput(e.target.value)}
        className="flex-1 bg-transparent px-3 text-sm outline-none placeholder:text-muted-foreground"
      />
    </div>
  );
}
